import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from "react-router-dom";
import { track } from "./utils/track";
import { C } from "./constants/baziData";
import { API } from "./constants/config";
import "./components/charts/charts.css";

// ── Layout ────────────────────────────────────────────────────────────────────
import ErrorBoundary from "./components/ErrorBoundary";
import Nav from "./components/Nav";

// ── Pages ─────────────────────────────────────────────────────────────────────
import LandingPage from "./components/LandingPage";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/AuthPages";
import AdminPage from "./pages/AdminPage";
import CommunityPage from "./pages/CommunityPage";
import VajraPortal from "./pages/vajra";
import LiuyaoPage from "./pages/LiuyaoPage";
import EnergyDashboard from "./pages/EnergyDashboard";
import EventDetailPage from "./pages/EventDetailPage";
import TeachingDetailPage from "./pages/TeachingDetailPage";
import VajraPublicCalendar from "./pages/VajraPublicCalendar";
// DharmaLibrary removed Phase 114 — consolidated into DharmaTeachingsHub
import VajraShop from "./pages/vajra/VajraShop";
import VajraShopProduct from "./pages/vajra/VajraShopProduct";
import OfferingDetailPage from "./pages/vajra/OfferingDetailPage";
import DharmaPathView from "./pages/vajra/DharmaPathView";
import MyJourney from "./pages/vajra/MyJourney";
import ActivityPage from "./pages/vajra/ActivityPage";
import PrivacyPage from "./pages/PrivacyPage";
import SEOToolPage from "./pages/SEOToolPage";
import TermsPage from "./pages/TermsPage";
import AboutPage from "./pages/AboutPage";

// ── Phase 38 extracted pages ──────────────────────────────────────────────────
import AuditPage from "./pages/AuditPage";
import ReportView from "./pages/ReportView";
import HistoryPage from "./pages/HistoryPage";
import PricingPage from "./pages/PricingPage";
import AccountPage from "./pages/AccountPage";
import ConsultantBookingPage from "./pages/ConsultantBookingPage";
import QimenPage from "./pages/QimenPage";
import SynastryPage from "./pages/SynastryPage";
import ToolsPage from "./pages/ToolsPage";
import OracleLanding from "./pages/OracleLanding";
import TemplesListPage from "./pages/TemplesListPage";
import TempleDetailPage from "./pages/TempleDetailPage";
import TopicHubPage from "./pages/TopicHubPage";
import TeachingsGateway from "./pages/vajra/TeachingsGateway";
import TeachingTopicPage from "./pages/vajra/TeachingTopicPage";
import HistoricalGalleryPage from "./pages/vajra/HistoricalGalleryPage";
import YuanqiObservationPage from "./pages/vajra/YuanqiObservationPage";
import SpecialSeriesPage from './pages/vajra/SpecialSeriesPage';

import NotFoundPage from "./pages/NotFoundPage";

import TeacherDetailPage from "./pages/vajra/TeacherDetailPage";
import PractitionerDashboard from "./pages/vajra/PractitionerDashboard";
import ProfileListPage from "./pages/ProfilesPage";        // Phase 104
import ProfileDetailPage from "./pages/ProfileDetailPage";    // Phase 104


function ProfileRedirect() {
  const [target, setTarget] = useState(null);
  useEffect(() => {
    const t = localStorage.getItem("lumina_token");
    if (!t) { setTarget("/profile/list"); return; }
    fetch("/api/profiles", { headers: { Authorization: "Bearer " + t } })
      .then(r => r.ok ? r.json() : [])
      .then(profiles => {
        const primary = profiles.find(p => p.is_primary);
        setTarget(primary ? "/profile/" + primary.id : "/profile/list");
      })
      .catch(() => setTarget("/profile/list"));
  }, []);
  if (!target) return React.createElement('div', { style: { padding: 40, textAlign: "center" } }, "Loading...");
  return React.createElement(Navigate, { to: target, replace: true });
}

function RetreatSlugRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/events/${slug}`} replace />;
}

function TeachingDetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/teaching/${id}`} replace />;
}

function TopicRedirect() {
  const { slug } = useParams();
  return <Navigate to={`/vajra/teachings/${slug}`} replace />;
}

export default function App(){
  return (
    <BrowserRouter>
      <AppShell/>
    </BrowserRouter>
  );
}

function AppShell(){
  const navigate = useNavigate();
  const location = useLocation();
  // Compatibility layer: legacy setPage("xxx") calls navigate to /xxx
  const setPage = (p) => {
    if (!p || p === "landing") { navigate("/"); return; }
    navigate("/" + p);
  };
  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Derive `page` token from URL so existing `page==="x"` checks keep working.
  const _seg = location.pathname.split("/").filter(Boolean);
  const page = _seg.length === 0 ? "oracle"
            : _seg[0];

  const [user,setUser]=useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [lang,setLang]=useState(()=>{
    const urlLang = new URLSearchParams(window.location.search).get('lang');
    if(urlLang==='en'||urlLang==='zh'){localStorage.setItem("lumina_lang",urlLang);return urlLang;}
    return localStorage.getItem("lumina_lang")||"en";
  });

  const handleSetLang = (l) => { setLang(l); localStorage.setItem("lumina_lang", l); };

  // Handle Stripe payment return — show inline toast, then force-refresh to /account
  const [paymentToast, setPaymentToast] = useState(null);
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const payment=params.get("payment");
    const product=params.get("product");
    if(payment==="success"){
      const messages = {
        liuyao:          { zh: "✓ 六爻次数已到账！", en: "✓ Liuyao credit added!" },
        oracle:          { zh: "✓ Oracle 问事次数已到账！", en: "✓ Oracle credit added!" },
        chart:           { zh: "✓ 命盘报告已解锁！", en: "✓ Chart report unlocked!" },
        chart_purchase:  { zh: "✓ 命盘报告已解锁！", en: "✓ Chart report unlocked!" },
        synastry:        { zh: "✓ 合盘已解锁！", en: "✓ Synastry unlocked!" },
        chart_renew:     { zh: "✓ 命盘年度更新已解锁！", en: "✓ Chart annual renewal unlocked!" },
        synastry_renew:  { zh: "✓ 合盘年度复查已解锁！", en: "✓ Synastry renewal unlocked!" },
        chat_access:     { zh: "✓ AI 对话已开通！", en: "✓ AI chat access granted!" },
      };
      const msg = messages[product];
      const text = msg ? (lang==="zh" ? msg.zh : msg.en)
        : (lang==="zh" ? "✓ 支付成功！刷新后可查看最新状态" : "✓ Payment successful! Refresh to see updated status");
      setPaymentToast(text);
      // Clean URL params
      const clean = new URL(window.location);
      clean.searchParams.delete("payment");
      clean.searchParams.delete("product");
      clean.searchParams.delete("session_id");
      window.history.replaceState({}, "", clean.toString());
      // Force reload to /account after showing toast briefly
      setTimeout(() => {
        setPaymentToast(null);
        window.location.href = "/account";
      }, 2500);
    }
  // eslint-disable-next-line
  },[]);
  const [selectedReport,setSelectedReport]=useState(null);
  const [resetToken,setResetToken]=useState(null);


  // Handle password reset token from email
  useEffect(()=>{
    const params=new URLSearchParams(window.location.search);
    const rt=params.get("reset_token");
    if(rt){
      setResetToken(rt);
      navigate("/reset-password", { replace: true });
    }
  // eslint-disable-next-line
  },[]);

  // Route firewall: if vajra token exists and visiting root, send to portal
  useEffect(()=>{
    if(location.pathname === "/" || location.pathname === "/landing"){
      const vajraToken = localStorage.getItem("vajra_token");
      if(vajraToken){
        navigate("/vajra", { replace: true });
      }
    }
  // eslint-disable-next-line
  },[]);

  useEffect(()=>{
    const token=localStorage.getItem("lumina_token");
    if(token){
      fetch(`${API}/auth/me`,{headers:{"Authorization":`Bearer ${token}`}})
        .then(r=>r.ok?r.json():null).then(d=>{if(d)setUser({email:d.email,tier:d.tier,is_admin:d.is_admin,token});}).catch(()=>{})
        .finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }
  },[]);

  // vajra subdomain: redirect root to /vajra
  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hostname === "vajra.lumarcai.com" && window.location.pathname === "/") {
      navigate("/vajra", { replace: true });
    }
  }, []);

  const onLogin=u=>{setUser(u);track("login_done",{tier:u?.tier});};
  const onLogout=()=>{localStorage.removeItem("lumina_token");setUser(null);navigate("/audit");};

  // Route isolation: /vajra/* and standalone pages get their own chrome, not LUMINA Nav/Footer
  const isVajraRoute = location.pathname.startsWith("/vajra");
  const isDashboardRoute = location.pathname === "/dashboard";
  const isRootRoute = location.pathname === "/";
  const hideNav = (page==="landing" && !isDashboardRoute) || isVajraRoute || isRootRoute || page==="puja" || page==="teaching" || page==="events" || page==="privacy" || page==="terms" || page==="about";
  const hideFooter = isVajraRoute || page==="landing" || isRootRoute || page==="events" || page==="about";

  return(
    <div style={{minHeight:"100vh",background:C.bg,color:C.mist,fontFamily:"'Inter','Montserrat',Georgia,sans-serif"}}>
      <style>{`*{box-sizing:border-box;margin:0;padding:0}body{background:${C.bg};font-family:'Inter',sans-serif}input,select,textarea{font-family:'Inter','Montserrat',sans-serif;font-size:15px}input:focus,select:focus,textarea:focus{border-color:rgba(212,175,55,.6)!important;outline:none;box-shadow:0 0 0 3px rgba(212,175,55,.10)!important}::-webkit-scrollbar{width:5px;background:transparent}::-webkit-scrollbar-thumb{background:rgba(6,78,59,.2);border-radius:3px}`}</style>
      {/* Payment success toast */}
      {paymentToast && (
        <div style={{
          position:"fixed", top:20, left:"50%", transform:"translateX(-50%)", zIndex:9999,
          background:`linear-gradient(135deg, ${C.gold}, #B8940A)`, color:"#fff",
          padding:"14px 32px", borderRadius:12, fontSize:15, fontWeight:600,
          boxShadow:"0 8px 32px rgba(212,175,55,.3)",
          animation:"fadeInDown .3s ease",
        }}>
          {paymentToast}
        </div>
      )}
      {!hideNav && <Nav page={page} setPage={setPage} user={user} onLogout={onLogout} lang={lang} setLang={handleSetLang}/>}
      <Routes>
        <Route path="/"          element={<LandingPage setPage={setPage} lang={lang} setLang={handleSetLang} user={user}/>}/>
        <Route path="/audit"     element={<AuditPage user={user} setPage={setPage} lang={lang}/>}/>
        <Route path="/login"     element={<LoginPage setPage={setPage} onLogin={onLogin} lang={lang}/>}/>
        <Route path="/register"  element={<RegisterPage setPage={setPage} onLogin={onLogin} lang={lang}/>}/>
        <Route path="/pricing"   element={<PricingPage user={user} setPage={setPage} lang={lang}/>}/>
        <Route path="/account"   element={<AccountPage user={user} authLoading={authLoading} onLogin={onLogin} lang={lang}/>}/>
        <Route path="/booking"   element={<ConsultantBookingPage lang={lang} user={user} setPage={setPage}/>}/>
        <Route path="/retreat" element={<Navigate to="/vajra/calendar" replace/>}/>
        <Route path="/forgot-password" element={<ForgotPasswordPage lang={lang} setPage={setPage}/>}/>
        <Route path="/reset-password"  element={resetToken
          ? <ResetPasswordPage lang={lang} setPage={setPage} resetToken={resetToken}/>
          : <Navigate to="/login" replace/>}/>
        <Route path="/admin"     element={<AdminPage user={user}/>}/>
        <Route path="/community" element={<CommunityPage lang={lang} setPage={setPage}/>}/>
        <Route path="/subscribe" element={<Navigate to="/vajra/subscribe" replace/>}/>
        <Route path="/vajra"        element={<ErrorBoundary><VajraPortal setPage={setPage} lang={lang} setLang={handleSetLang}/></ErrorBoundary>}/>
        <Route path="/vajra/subscribe" element={<ErrorBoundary><VajraPortal setPage={setPage} lang={lang} setLang={handleSetLang}/></ErrorBoundary>}/>
        <Route path="/vajra/teachings" element={<ErrorBoundary><TeachingsGateway lang={lang}/></ErrorBoundary>}/>
        <Route path="/vajra/teachings/:slug" element={<ErrorBoundary><TeachingTopicPage lang={lang}/></ErrorBoundary>}/>
        <Route path="/vajra/:tab"   element={<ErrorBoundary><VajraPortal setPage={setPage} lang={lang} setLang={handleSetLang}/></ErrorBoundary>}/>
        <Route path="/events/tara-2026" element={<Navigate to="/vajra/cittamani-tara-2026" replace/>}/>
        <Route path="/liuyao"    element={<LiuyaoPage user={user} lang={lang}/>}/>
        <Route path="/dharma"    element={<Navigate to="/vajra" replace/>}/>
        <Route path="/puja"      element={<Navigate to="/vajra" replace/>}/>
        <Route path="/events"          element={<Navigate to="/vajra/calendar" replace/>}/>
        <Route path="/events/:slug"    element={<EventDetailPage lang={lang} setPage={setPage}/>}/>
        {/* Phase 104: smart redirect to primary profile */}
        <Route path="/profile"         element={<ProfileRedirect />}/>

        <Route path="/profile/list"    element={<ProfileListPage user={user} lang={lang}/>}/>
        <Route path="/profile/:id"     element={<ProfileDetailPage user={user} lang={lang}/>}/>
        <Route path="/history"   element={<Navigate to="/profile" replace/>}/>
        <Route path="/report"    element={selectedReport
          ? <ReportView result={selectedReport} onReset={()=>navigate("/history")} birthYear={selectedReport.birthYear} lang={lang} setPage={setPage} user={user}/>
          : <Navigate to="/history" replace/>}/>
        <Route path="/report/:id" element={<ReportLoader lang={lang} setPage={setPage} user={user} />} />
        <Route path="/dashboard"       element={<EnergyDashboard user={user} setPage={setPage} lang={lang}/>}/>
        <Route path="/teaching/:id"    element={<TeachingDetailPage lang={lang}/>}/>
        <Route path="/events/tara-2026" element={<Navigate to="/vajra/cittamani-tara-2026" replace/>}/>
        <Route path="/vajra/:tab"   element={<ErrorBoundary><VajraPortal setPage={setPage} lang={lang}/></ErrorBoundary>}/>
        <Route path="/vajra/library"   element={<Navigate to="/vajra" replace/>}/>
        <Route path="/vajra/library/:id" element={<TeachingDetailRedirect/>}/>
        <Route path="/vajra/topic/:slug"  element={<TopicRedirect/>}/>
        <Route path="/vajra/shop"      element={<VajraShop lang={lang}/>}/>
        <Route path="/vajra/shop/:id"  element={<VajraShopProduct lang={lang}/>}/>
        <Route path="/vajra/offering/:id" element={<OfferingDetailPage lang={lang}/>}/>
        <Route path="/vajra/calendar"  element={<VajraPublicCalendar lang={lang}/>}/>
        <Route path="/vajra/gallery"  element={<HistoricalGalleryPage lang={lang}/>}/>
        <Route path="/vajra/cittamani-tara-2026" element={<Navigate to="/vajra/special/cittamani-tara-2026" replace/>}/>
        <Route path="/vajra/special/:slug" element={<SpecialSeriesPage lang={lang}/>}/>
        <Route path="/vajra/teacher/:id"   element={<TeacherDetailPage lang={lang}/>}/>
        <Route path="/vajra/dharma-path/:tradition" element={<DharmaPathView lang={lang}/>}/>
        <Route path="/vajra/my-path"               element={<DharmaPathView isMyPath={true} lang={lang}/>}/>
        <Route path="/vajra/my-journey"            element={<MyJourney lang={lang}/>}/>
        <Route path="/vajra/me"                    element={<PractitionerDashboard lang={lang}/>}/>
        <Route path="/vajra/activities"            element={<ActivityPage lang={lang}/>}/>
        <Route path="/vajra/activities/:id"        element={<ActivityPage lang={lang}/>}/>
        <Route path="/privacy" element={<PrivacyPage/>}/>
        <Route path="/terms"   element={<TermsPage/>}/>
        <Route path="/about"   element={<AboutPage lang={lang}/>}/>
        <Route path="/qimen"   element={<QimenPage user={user} lang={lang} setPage={setPage}/>}/>
        <Route path="/synastry" element={<SynastryPage user={user} lang={lang} setPage={setPage}/>}/>
        <Route path="/tools"    element={<ToolsPage/>}/>
        <Route path="/almanac"  element={<SEOToolPage tool="almanac"/>}/>
        <Route path="/dreams"   element={<SEOToolPage tool="dreams"/>}/>
        <Route path="/oracle"   element={<OracleLanding lang={lang}/>}/>
        <Route path="/naming"   element={<SEOToolPage tool="naming"/>}/>
        <Route path="/vajra/yuanqi-observation" element={<YuanqiObservationPage lang={lang}/>}/>
        <Route path="/vajra/temples"     element={<TemplesListPage setPage={setPage}/>}/>
        <Route path="/vajra/temples/:id" element={<TempleDetailPage setPage={setPage} lang={lang}/>}/>
        <Route path="*" element={<NotFoundPage/>}/>
      </Routes>
      {!hideFooter && (
        <div style={{textAlign:"center",padding:"28px",fontSize:11,color:C.mist4,borderTop:"1px solid rgba(0,0,0,.08)",marginTop:40,letterSpacing:".08em",fontFamily:"'Inter','Montserrat',sans-serif",fontStyle:"italic"}}>
          {lang==="zh" ? "© 2026 澄光命盘·LUMINAARC · 澄光映照，自见本性 · Toronto" : "© 2026 LUMINAARC · 澄光映照，自见本性 · Toronto"}
        </div>
      )}
    </div>
  );
}

const mapBackendToLocalShape = (backend) => {
  const s = backend.energy_score?.scores || {};
  const es = backend.energy_score?.elements || {};
  const pctCN = backend.elements?.percentages || {};
  const pct = {
    Wood:  pctCN["木"] ?? 0,
    Fire:  pctCN["火"] ?? 0,
    Earth: pctCN["土"] ?? 0,
    Metal: pctCN["金"] ?? 0,
    Water: pctCN["水"] ?? 0,
  };
  const dominantCN  = es.dominant  || "";
  const deficientCN = (es.deficient_elements && es.deficient_elements[0]) || es.deficient || "";
  const CN2EN = { "木":"Wood","火":"Fire","土":"Earth","金":"Metal","水":"Water" };
  const dominant  = CN2EN[dominantCN]  || dominantCN  || "Earth";
  const lacking   = CN2EN[deficientCN] || deficientCN || "Metal";

  const deity1 = (backend.deity_recommendations || [])[0] || {};
  const tierRaw = backend.energy_score?.tier || {};

  return {
    overall:     s.overall_alignment  ?? 50,
    vitality:    s.vitality_index     ?? 50,
    stress:      s.stress_blockage    ?? 50,
    momentum:    s.cycle_momentum     ?? 50,
    relational:  s.relational_field   ?? 50,
    pct,
    dominant,
    lacking,
    tier: {
      color: tierRaw.color || "#C9A96E",
      label: tierRaw.label || "—",
    },
    proto: {
      g:      dominant,
      deity:  deity1.deity_en || deity1.deity || "—",
      p:      deity1.practice || "",
      d:      deity1.diet     || "",
    },
  };
};

const mapBackendPillars = (backendPillars) => {
  if (!backendPillars) return null;
  const out = {};
  for (const p of ["year","month","day","hour"]) {
    const bp = backendPillars[p] || {};
    out[p] = {
      gz: bp.ganzhi || "",
      en: bp.en     || "",
      se: bp.stem_element   || "",
      be: bp.branch_element || "",
    };
  }
  return out;
};

function ReportLoader({ lang, setPage, user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError("");
    const token = localStorage.getItem("lumina_token");
    fetch(`/api/profiles/reports/${id}`, {
      headers: {
        "Authorization": `Bearer ${token || ""}`,
        "Content-Type": "application/json"
      }
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(lang === "zh" ? "报告加载失败或无权查看" : "Failed to load report");
        }
        return res.json();
      })
      .then(data => {
        const backend = data.report || data;
        if (data.report) {
          backend._report_number = data.report_number || backend.meta?.report_id || id;
          backend._user_tier = data.user_tier || "";
          backend._credentials = data.credentials || [];
          backend._upcoming_events = data.upcoming_events || [];
          backend.qa_answers = data.qa_answers || [];
          backend._energy_match = data.energy_match || null;
        } else {
          backend._report_number = id;
        }

        const mergedResult = {
          pillars: mapBackendPillars(backend.pillars),
          sc:      mapBackendToLocalShape(backend),
          name:    backend.client || backend.name || "",
          location: backend.location || "",
          concern:  backend.concern || "",
          gender:   (backend.gender || "M").toUpperCase(),
          ts:       new Date(backend.generated || Date.now()),
          rid:      backend._report_number || id,
          _source:  "backend",
          _backend: backend,
          _form: backend._form || {
            year: backend.birth_year || backend.bazi?.pillars?.year?.year || 1980,
            month: backend.birth_month || 1,
            day: backend.birth_day || 1,
            hour: backend.birth_hour || 12,
            minute: backend.birth_minute || 0,
            name: backend.client || backend.name || "",
            location: backend.location || "",
            concern: backend.concern || "",
            gender: (backend.gender || "M").toUpperCase() === "FEMALE" ? "F" : "M",
            lang
          }
        };

        setReportData(mergedResult);
      })
      .catch(err => {
        setError(err.message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id, lang]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 16, color: C.mist4 }}>{lang === "zh" ? "正在加载报告..." : "Loading report..."}</div>
      </div>
    );
  }

  if (error || !reportData) {
    return (
      <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 16, color: C.mist3 }}>{error || (lang === "zh" ? "报告不存在" : "Report not found")}</div>
        <button onClick={() => navigate("/profile")} style={{ padding: "8px 24px", borderRadius: 8, border: `1px solid ${C.gold}`, background: "transparent", color: C.gold, cursor: "pointer", fontSize: 13 }}>
          {lang === "zh" ? "返回档案" : "Back to Profiles"}
        </button>
      </div>
    );
  }

  const birthYear = reportData._form?.year || 1980;

  return (
    <ReportView
      result={reportData}
      onReset={() => navigate("/profile")}
      birthYear={birthYear}
      lang={lang}
      setPage={setPage}
      user={user}
      API="/api"
    />
  );
}
